/**
 * The domain type surface.
 *
 * Built from `tradecoach-saas/docs/TYPE-SURFACE-AUDIT.md` (Phase 0 deliverable E),
 * which answered one question per field: typed column, typed JSONB member, or
 * genuinely freeform. Read that before changing anything here.
 *
 * THREE RULES THIS FILE ENFORCES
 *
 * 1. FIELDS, NOT THE LEGACY NESTING. The old client carries `t.core.netPnl`,
 *    `t.risk.riskPoints`, `t.tagging.patternId` — but the DATABASE is already
 *    flat (`net_pnl`, `risk_points`, `pattern_id`). That nesting is in-memory
 *    only, and mistaking it for schema is exactly what made rebuild decision
 *    D12 wrong. Model the database.
 *
 * 2. NO ORPHAN COLUMNS. Five columns exist in the DDL that nothing reads or
 *    writes; two of them are worse than dead, because their live values sit in
 *    `extras.rulesX` beside an empty column. They are listed in ORPHAN_COLUMNS
 *    below as documentation, and are deliberately absent from the types.
 *
 * 3. EXHAUSTIVE PERSISTENCE. Every entity has a `*_COLUMNS` map typed
 *    `{ [K in keyof T]-?: string }`. Add a field to the type without adding it
 *    to the map and the build FAILS. This is the fix for the container trap:
 *    in the old client, adding a key to `t.core` without adding a column meant
 *    the value was silently dropped on save, and the loss looked like a
 *    plausible number rather than an error.
 */

// ---------------------------------------------------------------------------
// Identifiers
// ---------------------------------------------------------------------------
// Branded so an account id cannot be passed where a pattern id is expected.
// Justified by history rather than taste: this codebase has shipped FK bugs
// (orphaned pattern_id / account_id / exit_model_id refs needing a repair pass
// on every save, and a global contracts catalogue where an uncatalogued root
// aborted a whole import batch — that last constraint is gone as of
// `20260906000006`, but the history is why this is here). One cast at the IO
// boundary buys that away.

declare const brand: unique symbol;
type Brand<T, B> = T & { readonly [brand]: B };

export type TradeId = Brand<string, 'TradeId'>;
export type AccountId = Brand<string, 'AccountId'>;
export type PatternId = Brand<string, 'PatternId'>;
export type ExitModelId = Brand<string, 'ExitModelId'>;
export type AdjustmentId = Brand<string, 'AdjustmentId'>;

/** Cast at the IO boundary only — never inside `domain/`. */
export const asTradeId = (s: string) => s as TradeId;
export const asAccountId = (s: string) => s as AccountId;
export const asPatternId = (s: string) => s as PatternId;
export const asExitModelId = (s: string) => s as ExitModelId;
export const asAdjustmentId = (s: string) => s as AdjustmentId;

/** `YYYY-MM-DD`. Distinct from a timestamp: the day boundary is a domain rule. */
export type IsoDate = Brand<string, 'IsoDate'>;
/** `HH:MM` or `HH:MM:SS`, in the user's configured session timezone. */
export type ClockTime = Brand<string, 'ClockTime'>;

// ---------------------------------------------------------------------------
// Enums — mirrored from the Postgres types
// ---------------------------------------------------------------------------
export type TradeSide = 'long' | 'short';
export type AccountType = 'combine' | 'funded' | 'personal' | 'sim-funded' | 'live-funded';
export type AccountStatus = 'active' | 'paused' | 'passed' | 'blown' | 'closed';
export type DrawdownType = 'trailing-intraday' | 'trailing-eod' | 'static';
export type DayBoundaryMode = 'calendar' | 'session';
export type PnlUnit = 'usd' | 'points' | 'ticks' | 'r';

/**
 * THE distinction four separate functions used to disagree about.
 *
 * `balance` — money that moved the ACCOUNT (payouts, deposits). Belongs in a
 *             balance, an equity curve, or a P&L total.
 * `pnl`     — out-of-pocket FEES (eval, reset, activation). Money that left the
 *             trader's bank, not the account. Must NOT reach a balance.
 *
 * Conflating these made "Total P&L" mean two different things on two surfaces,
 * and fed an annualisation that multiplied a $165 fee into a ~$2,000 Calmar
 * distortion. Making it a type is the point of the rebuild in one line.
 */
export type AdjustmentScope = 'balance' | 'pnl';

export const PROP_FEE_TYPES = ['Eval fee', 'Reset fee', 'Activation fee'] as const;
export type PropFeeType = (typeof PROP_FEE_TYPES)[number];

// ---------------------------------------------------------------------------
// Trade
// ---------------------------------------------------------------------------
export interface Trade {
  id: TradeId;
  accountId: AccountId | null;

  // -- execution ------------------------------------------------------------
  date: IsoDate;
  /** Precomputed session-day. Null when the account predates session mode. */
  sessionDate: IsoDate | null;
  timeIn: ClockTime | null;
  timeOut: ClockTime | null;
  /** Root symbol, FK to the global contracts catalogue (e.g. `MES`). */
  contract: string;
  /** Exact month traded (e.g. `MESU6`) when captured — drives exact-contract bars. */
  contractFull: string | null;
  side: TradeSide;
  qty: number;
  entry: number | null;
  exit: number | null;

  // -- money ----------------------------------------------------------------
  points: number | null;
  grossPnl: number | null;
  fees: number;
  feesAuto: boolean;
  netPnl: number | null;

  // -- excursion (MAE/MFE) --------------------------------------------------
  maePoints: number | null;
  mfePoints: number | null;
  maeSecs: number | null;
  mfeSecs: number | null;

  // -- risk -----------------------------------------------------------------
  riskPoints: number | null;
  rrPlanned: number | null;
  rrRealized: number | null;
  plannedStop: number | null;
  plannedTarget: number | null;
  riskAtEntry: number | null;

  // -- tagging / context ----------------------------------------------------
  patternId: PatternId | null;
  exitModelId: ExitModelId | null;
  grade: string;
  sessionLabel: string;
  marketRegime: string;
  newsEvent: string;
  mood: string;
  mistakeTags: string[];
  /** User tag ids from the per-user registry. */
  tags: string[];

  // -- typed JSONB ----------------------------------------------------------
  confluences: Record<string, ConfluenceMark>;
  booleans: Record<string, boolean>;

  // -- derived / bookkeeping ------------------------------------------------
  controllableLossAmount: number;
  screenshots: string[];
  recordings: string[];
  notes: string;
  annotated: boolean;
  imported: boolean;
  sourceAccount: string | null;
  syntheticClose: boolean;
  flagged: boolean;
  flagReasons: string[];
}

export interface ConfluenceMark {
  active: boolean;
  /**
   * ⚠ OPTIONAL, and that is a schema fact rather than a convenience.
   *
   * The old client stopped writing per-confluence notes when confluences gained
   * one level of children — "specificity now comes from picking a child" — and
   * `commitTrade` now stores exactly `{ active: true }`. Legacy rows still carry
   * a `notes` string, so the field cannot be dropped; requiring it would make the
   * trade form write a `notes: ''` the old client never writes, on every
   * confluence, on every save.
   */
  notes?: string;
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------
/**
 * Where an account's rules came from. See `Account.source`.
 *
 * ⚠ `verifiedDate` is the field that makes the whole block honest: it is when a
 * human last checked the numbers against the firm's page, and it is `null` on
 * everything the setup modal writes — because nobody checked. Beta removed
 * per-firm rule templates for exactly this reason, so never default it to today.
 */
export interface AccountSource {
  url: string;
  name: string | null;
  verifiedDate: string | null;
  /** Old per-firm-template lineage. Absent on anything written since. */
  plan?: string;
  templateId?: string;
}

export interface Account {
  id: AccountId;
  name: string;
  firm: string;
  accountType: AccountType;
  accountSize: number;
  startingBalance: number;
  currentBalance: number;
  autoBalance: boolean;
  status: AccountStatus;
  /** Matches the broker export's account column, for import routing. */
  externalId: string;
  /**
   * Where this account's RULES came from — a JSONB block, not a string.
   *
   * 🚨 THIS WAS TYPED `string | null` AND LOADED WITH `String(row['source'])`,
   * which turns the stored object into the literal text `"[object Object]"`.
   * Two of Josh's accounts carry a real one:
   *
   *   { name: 'Lucid', url: 'https://apextraderfunding.com/…', verifiedDate: null }
   *   { name: 'Apex Trader Funding — EOD Evaluations', plan: '50k Eval (EOD)',
   *     templateId: 'apex-50k-eval-eod', url: '…', verifiedDate: '2026-06-03' }
   *
   * Nothing read it and `saveAccount` never wrote it (D62), so nothing was
   * visibly wrong — but the "Set up account" modal collects a firm rules URL and
   * writes exactly this block, so the mangling would have gone live with it.
   *
   * ⚠ TWO SHAPES, and both are in his data. The five-key one is the old per-firm
   * template lineage, which beta REMOVED (stale firm rules silently produce wrong
   * drawdown math); the three-key one is what the setup modal writes today.
   * `plan` and `templateId` are optional so an old row survives a round trip
   * rather than being narrowed away on save.
   */
  source: AccountSource | null;
  /** Carry-in peak for accounts added mid-life. Null = walk the trade timeline. */
  initialPeakBalance: number | null;
  /**
   * ⚠ Its trades stay LISTED but never move a number — the SIM / Replay opt-out.
   *
   * Has no column: it rides `extras`, exactly like the rules pair below. The
   * first loader dropped it, so every excluded account read as included. Three
   * of the beta user's accounts carry it and one was a third of a week's volume,
   * which inflated every P&L, win rate and drawdown figure in the app.
   */
  excludeFromMetrics: boolean;
  /** De-clutter only — an archived account still counts. Also rides `extras`. */
  archived: boolean;
  /**
   * ── The COST fields. All three ride `extras` — none has a column. ──
   *
   * The old client is explicit: "Cadence + amount ride `extras` (not in
   * `_ACCOUNT_KNOWN`)". They drive the Economics tab's Recurring Burn and its
   * rebill-risk warning, and `feeWaived` is the "N free" count under Evals
   * Started.
   *
   * ⚠ `feeWaived` is a SEPARATE fact from `feeAmount === 0`. An account whose fee
   * was waived cost nothing by agreement; one with no fee recorded may simply
   * never have had the cost entered. Collapsing them would let missing data
   * masquerade as a free account and flatter cost-per-funded.
   */
  feeWaived: boolean;
  /** `'monthly'` drives Recurring Burn; anything else is a one-off. */
  feeCadence: string;
  feeAmount: number;
  /**
   * The trader waved away the "N fields missing" prompt on this account's card.
   *
   * ⚠ RIDES `extras`, and it is the SHIPPING CLIENT'S FLAG — both clients read
   * one database, so a prompt Josh dismissed in beta was coming back on every
   * card here because nothing read it (#73). The writer already preserved it;
   * preserving a field nobody reads is indistinguishable from dropping it, from
   * where the user is sitting.
   *
   * ⚠ NOT IN `ownedAccountFields`. The edit form does not own it — only the ✕ on
   * the prompt writes it, through its own narrow save. D62: never send a field
   * you are not deliberately writing.
   */
  setupNudgeDismissed: boolean;
  rules: AccountRules;
  adjustments: AccountAdjustment[];
}

/**
 * Every key has a home. In the old client, `rules` sat inside `_ACCOUNT_KNOWN`,
 * so any sub-key without a column was dropped on save and `_extractExtras`
 * could not rescue it — which is precisely how `consistencyScope` and
 * `dllEnforcement` vanished on reload while two readers defaulted them
 * OPPOSITELY. That was patched with an `extras.rulesX` shim; here the type IS
 * the contract and ACCOUNT_RULES_COLUMNS makes it exhaustive.
 */
export interface AccountRules {
  maxDDType: DrawdownType | null;
  maxDDValue: number;
  lockMechanic: string | null;
  lockPad: number | null;
  dailyLossLimit: number;
  profitTarget: number;
  consistencyRulePct: number;
  /** Which trades the consistency rule applies to. Defaulted OPPOSITELY by two
   *  readers when it silently failed to persist — never leave this optional. */
  consistencyScope: 'always' | 'eval-only' | 'funded-only' | 'none';
  /**
   * ⚠ `'optional'` IS THE VALUE THE SHIPPING CLIENT ACTUALLY WRITES, and this
   * union did not have it. Every firm shape sets `dllEnforcement: 'optional'`,
   * as does the completeness filler that repairs rules blocks on load — so it is
   * what is in the live data, and a type listing only hard/soft/none was a lie
   * that no reader had caught yet because nothing here reads the field.
   *
   * Found while writing the account editor, which carries this key across a save
   * without a form field: `?? 'optional'` is the default that preserves today's
   * behaviour (the old client's daily-limit check is `!== 'none'`), and the type
   * rejected the only correct answer.
   */
  dllEnforcement: 'hard' | 'soft' | 'optional' | 'none';
  minTradingDays: number;
  payoutSchedule: string;
  payoutsTaken: number;
  maxContracts: number;
  eodFlatRule: boolean;
  scalingRules: string;
  prohibitedProducts: string[];
}

export interface AccountAdjustment {
  id: AdjustmentId;
  accountId: AccountId;
  date: IsoDate;
  /** Free text; `PROP_FEE_TYPES` membership decides the AdjustmentScope. */
  type: string;
  amount: number;
  note: string;
}

// ---------------------------------------------------------------------------
// Pattern / ExitModel
// ---------------------------------------------------------------------------
export interface Pattern {
  id: PatternId;
  name: string;
  category: string;
  description: string;
  criteria: string;
  sessions: string[];
  regimes: string[];
  /**
   * ⚠ NUMBERS — `0` = Sunday … `6` = Saturday.
   *
   * This was declared `string[]` and it was wrong. The DDL says `INTEGER[]`, and
   * Josh's VWAP PDC pattern stores `[1,2,3,4,5]` — checked against the row, not
   * the type. Writing `['Mon','Tue']` into that column fails at the database, and
   * writing `['1','2']` would be accepted by PostgREST and then compare unequal
   * to the integers every reader uses.
   *
   * The global filter's `daysOfWeek` facet already uses integers, so the two
   * would silently never match either.
   */
  daysOfWeek: number[];
  priorDayRel: string | null;
  news: string | null;
  entryTrigger: string | null;
  stopRule: string | null;
  targetRule: string | null;
  scaleOutRules: string | null;
  invalidation: string | null;
  screenshots: string[];
}

/** Note: these three are SCALARS, not containers — no trap here. */
export interface ExitModel {
  id: ExitModelId;
  name: string;
  stopRule: string;
  targetRule: string;
  scaleOutRule: string;
}

// ---------------------------------------------------------------------------
// Exhaustive persistence maps
// ---------------------------------------------------------------------------
// `-?` strips optionality so an optional field cannot escape the check. Adding
// a field above without adding it here is a COMPILE ERROR, which is the whole
// mechanism: it converts the old client's silent-drop failure into a loud one.
//
// `null` means "not persisted directly" and must carry a reason.

// ⚠ `as const satisfies`, not a type ANNOTATION. The difference is load-bearing.
//
// Annotated, every value widens to `string | null`, so `TRADE_COLUMNS.tags` is
// `string | null` rather than `null` — and nothing can tell at compile time which
// fields actually have a column. A projection could then legally include `tags`,
// type-check, and return rows with no tags in them.
//
// `as const satisfies` keeps the exhaustiveness check (a new field with no entry
// is still a build error) AND preserves the literal types, which is what makes
// `TradeColumnKey` below possible.
export const TRADE_COLUMNS = {
  id: 'id', accountId: 'account_id', date: 'date', sessionDate: 'session_date',
  timeIn: 'time_in', timeOut: 'time_out', contract: 'contract',
  contractFull: 'contract_full', side: 'side', qty: 'qty', entry: 'entry', exit: 'exit',
  points: 'points', grossPnl: 'gross_pnl', fees: 'fees', feesAuto: 'fees_auto',
  netPnl: 'net_pnl', maePoints: 'mae_points', mfePoints: 'mfe_points',
  maeSecs: 'mae_secs', mfeSecs: 'mfe_secs', riskPoints: 'risk_points',
  rrPlanned: 'rr_planned', rrRealized: 'rr_realized', plannedStop: 'planned_stop',
  plannedTarget: 'planned_target', riskAtEntry: 'risk_at_entry',
  patternId: 'pattern_id', exitModelId: 'exit_model_id', grade: 'grade',
  sessionLabel: 'session_label', marketRegime: 'market_regime',
  newsEvent: 'news_event', mood: 'mood', mistakeTags: 'mistake_tags',
  tags: null, // rides `extras` — a per-user registry, deliberately not a column
  confluences: 'confluences', booleans: 'booleans',
  controllableLossAmount: 'controllable_loss_amount',
  screenshots: 'screenshots', recordings: 'recordings', notes: 'notes',
  annotated: 'annotated', imported: 'imported', sourceAccount: 'source_account',
  syntheticClose: 'synthetic_close', flagged: 'flagged', flagReasons: 'flag_reasons',
} as const satisfies { [K in keyof Trade]-?: string | null };

/**
 * Every `Trade` field that HAS a real column — i.e. everything except `tags`,
 * which rides `extras`.
 *
 * This is what makes a column projection checkable at compile time: a projection
 * key set is `readonly TradeColumnKey[]`, so including `tags` is a build error
 * rather than a select string that silently returns rows with no tags.
 */
export type TradeColumnKey = {
  [K in keyof typeof TRADE_COLUMNS]: (typeof TRADE_COLUMNS)[K] extends string ? K : never;
}[keyof typeof TRADE_COLUMNS];

export const ACCOUNT_COLUMNS: { [K in keyof Account]-?: string | null } = {
  id: 'id', name: 'name', firm: 'firm', accountType: 'account_type',
  accountSize: 'account_size', startingBalance: 'starting_balance',
  currentBalance: 'current_balance', autoBalance: 'auto_balance', status: 'status',
  externalId: 'external_id', source: 'source',
  initialPeakBalance: 'initial_peak_balance',
  // Un-columned on purpose, the same shim as the rules pair: both live in
  // `extras`. Declared here as explicitly un-columned rather than quietly
  // absent, so the exhaustive map keeps forcing the loader to handle them.
  excludeFromMetrics: null,
  archived: null,
  // Same shim again — the old client keeps all three out of `_ACCOUNT_KNOWN`, so
  // they have never had columns. The exhaustive map is what forced this file to
  // be edited when they were added, which is the point of it.
  feeWaived: null,
  feeCadence: null,
  feeAmount: null,
  setupNudgeDismissed: null, // `extras`, like the four above
  rules: null,       // decomposed — see ACCOUNT_RULES_COLUMNS
  adjustments: null, // separate table, account_adjustments
};

export const ACCOUNT_RULES_COLUMNS: { [K in keyof AccountRules]-?: string | null } = {
  maxDDType: 'max_dd_type', maxDDValue: 'max_dd_value',
  lockMechanic: 'lock_mechanic', lockPad: 'lock_pad',
  dailyLossLimit: 'daily_loss_limit', profitTarget: 'profit_target',
  consistencyRulePct: 'consistency_rule_pct',
  // No column in the legacy schema — this pair is what vanished on reload.
  // Additive migrations give them real columns (D6); until then the loader
  // must read them, so they are declared here as explicitly un-columned rather
  // than quietly absent.
  consistencyScope: null,
  dllEnforcement: null,
  minTradingDays: 'min_trading_days', payoutSchedule: 'payout_schedule',
  payoutsTaken: 'payouts_taken', maxContracts: 'max_contracts',
  eodFlatRule: 'eod_flat_rule', scalingRules: 'scaling_rules',
  prohibitedProducts: 'prohibited_products',
};

export const EXIT_MODEL_COLUMNS: { [K in keyof ExitModel]-?: string | null } = {
  id: 'id', name: 'name', stopRule: 'stop_rule', targetRule: 'target_rule',
  scaleOutRule: 'scale_out_rule',
};

/**
 * Columns in the DDL that nothing reads or writes. Recorded so nobody
 * "discovers" one and models it. Dropping them is destructive, so it waits for
 * cutover (D6).
 */
export const ORPHAN_COLUMNS = {
  'accounts.lock_threshold': 'dead on both sides — no field in any casing',
  'accounts.current_dd_remaining': 'column never written; value lives in extras.rulesX',
  'accounts.profit_progress': 'column never written; value lives in extras.rulesX',
  'trades.stop_honored_flag': 'stripped by migration v2.6.4 as a vestigial twin of booleans.stopHonored',
  'trades.dollars_per_point': 'deliberately no longer written (Phase 2 audit 1.2)',
} as const;
